/**
 * In-memory storage adapter for development/testing
 */

import type { StorageAdapter, AgentDetectionEvent, AgentSession } from './types';

export class MemoryStorageAdapter implements StorageAdapter {
  private events: Map<string, AgentDetectionEvent> = new Map();
  private sessions: Map<string, AgentSession> = new Map();
  private eventTimeline: Array<{ timestamp: number; eventId: string }> = [];
  private maxEvents: number = 1000;
  private maxSessions: number = 100;

  async storeEvent(event: AgentDetectionEvent): Promise<void> {
    const eventKey = `${event.timestamp}:${event.eventId}`;
    this.events.set(eventKey, event);
    
    // Add to timeline
    this.eventTimeline.push({
      timestamp: Date.parse(event.timestamp),
      eventId: eventKey,
    });
    
    // Sort timeline by timestamp
    this.eventTimeline.sort((a, b) => b.timestamp - a.timestamp);
    
    // Maintain size limit
    if (this.eventTimeline.length > this.maxEvents) {
      const removed = this.eventTimeline.splice(this.maxEvents);
      removed.forEach(item => this.events.delete(item.eventId));
    }
  }

  async getRecentEvents(limit: number = 100): Promise<AgentDetectionEvent[]> {
    const recent = this.eventTimeline.slice(0, limit);
    return recent
      .map(item => this.events.get(item.eventId))
      .filter((event): event is AgentDetectionEvent => event !== undefined);
  }

  async getSessionEvents(sessionId: string): Promise<AgentDetectionEvent[]> {
    const events: AgentDetectionEvent[] = [];
    for (const event of this.events.values()) {
      if (event.sessionId === sessionId) {
        events.push(event);
      }
    }
    return events.sort((a, b) => 
      Date.parse(b.timestamp) - Date.parse(a.timestamp)
    );
  }

  async storeSession(session: AgentSession): Promise<void> {
    this.sessions.set(session.sessionId, session);
    
    // Maintain size limit
    if (this.sessions.size > this.maxSessions) {
      // Remove oldest sessions
      const sortedSessions = Array.from(this.sessions.entries())
        .sort((a, b) => Date.parse(b[1].lastSeen) - Date.parse(a[1].lastSeen));
      
      const toRemove = sortedSessions.slice(this.maxSessions);
      toRemove.forEach(([id]) => this.sessions.delete(id));
    }
  }

  async getSession(sessionId: string): Promise<AgentSession | null> {
    return this.sessions.get(sessionId) || null;
  }

  async getRecentSessions(limit: number = 10): Promise<AgentSession[]> {
    const sorted = Array.from(this.sessions.values())
      .sort((a, b) => Date.parse(b.lastSeen) - Date.parse(a.lastSeen));
    return sorted.slice(0, limit);
  }

  async cleanup(olderThan: Date): Promise<void> {
    const cutoff = olderThan.getTime();
    
    // Clean up events
    this.eventTimeline = this.eventTimeline.filter(item => {
      if (item.timestamp < cutoff) {
        this.events.delete(item.eventId);
        return false;
      }
      return true;
    });
    
    // Clean up sessions
    for (const [id, session] of this.sessions.entries()) {
      if (Date.parse(session.lastSeen) < cutoff) {
        this.sessions.delete(id);
      }
    }
  }
}